import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { MONTHS } from "@/components/bizstats/KpiTab";

const sel = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";

const MonthGrid = ({ title, rows, months, total, testid }) => (
  <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid={testid}>
    <h2 className="px-5 pt-4 pb-2 font-medium text-[#45423C]">{title}</h2>
    <div className="overflow-x-auto max-h-[480px]">
      <table className="w-full text-xs">
        <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60] sticky top-0">
          <tr><th className="px-3 py-2 text-left">Όνομα</th>{MONTHS.map((m) => <th key={m} className="px-2 py-2 text-right">{m}</th>)}<th className="px-2 text-right">Σύνολο</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-t border-[#E3DED4]" data-testid={`${testid}-row`}>
              <td className="px-3 py-1.5 font-medium text-[#45423C] max-w-[260px] truncate" title={r.name}>{r.name}</td>
              {Object.keys(r.months).map((m) => <td key={m} className="px-2 text-right tabular whitespace-nowrap">{r.months[m] ? fmtEuro(r.months[m]) : "—"}</td>)}
              <td className="px-2 text-right tabular font-semibold whitespace-nowrap">{fmtEuro(r.total)}</td>
            </tr>
          ))}
          <tr className="bg-[#45423C] text-white font-semibold" data-testid={`${testid}-total`}>
            <td className="px-3 py-1.5">Σύνολο</td>
            {Object.keys(months).map((m) => <td key={m} className="px-2 text-right tabular whitespace-nowrap">{fmtEuro(months[m])}</td>)}
            <td className="px-2 text-right tabular whitespace-nowrap">{fmtEuro(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

export const SpendTab = ({ year }) => {
  const [kind, setKind] = useState("all");
  const [group, setGroup] = useState("operating");
  const [category, setCategory] = useState("");
  const { data } = useQuery({
    queryKey: ["erp-stats", year, kind, group, category],
    queryFn: async () => (await api.get("/erp/stats", { params: { year, kind, group, category } })).data,
  });
  const gt = data?.group_totals || {};
  return (
    <div className="space-y-6" data-testid="bs-spend-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["operating", "Λειτουργικά"], ["capex", "Επενδύσεις (FF&E, έργα, πάγια)"], ["owner", "Ιδιοκτήτη / SeaNomad / λοιπά"]].map(([k, l]) => (
          <div key={k} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`bs-group-${k}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
            <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{fmtEuro(gt[k] || 0)}</div>
          </div>
        ))}
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="bs-filtered-total">
          <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">Επιλογή φίλτρων</div>
          <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{fmtEuro(data?.total || 0)}</div>
          <div className="text-xs text-[#6F6A60]">{data?.docs || 0} παραστατικά</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <select data-testid="bs-filter-kind" className={sel} value={kind} onChange={(e) => { setKind(e.target.value); setCategory(""); }}>
          <option value="all">Αγορές + Δαπάνες</option><option value="purchases">Μόνο Αγορές</option><option value="expenses">Μόνο Δαπάνες</option>
        </select>
        <select data-testid="bs-filter-group" className={sel} value={group} onChange={(e) => { setGroup(e.target.value); setCategory(""); }}>
          <option value="operating">Λειτουργικά</option><option value="capex">Επενδύσεις</option><option value="owner">Ιδιοκτήτη / λοιπά</option><option value="all">Όλα</option>
        </select>
        <select data-testid="bs-filter-category" className={sel} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Όλες οι κατηγορίες</option>
          {(data?.category_options || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {data && <MonthGrid title="Ανά κατηγορία και μήνα" rows={data.categories} months={data.months} total={data.total} testid="bs-cat-grid" />}
      {data && <MonthGrid title="Κορυφαίοι προμηθευτές (50) ανά μήνα" rows={data.suppliers} months={data.months} total={data.total} testid="bs-sup-grid" />}
      <p className="text-xs text-[#6F6A60]">Καθαρά ποσά. Τα πιστωτικά αφαιρούνται. Ο τελευταίος πίνακας δείχνει τους 50 μεγαλύτερους προμηθευτές· η γραμμή Σύνολο αφορά όλους.</p>
    </div>
  );
};
